import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutGrid, Activity, ShoppingCart, MapPin, Receipt, Store, Users, Grid3x3,
  Truck, CreditCard, Bell, MonitorSmartphone, Settings, type LucideIcon,
} from 'lucide-react';
import { adminApi } from '../lib/api';
import wheezW from '../assets/wheez-w-red.png';

const NAV: { label: string; to: string; icon: LucideIcon; countKey?: keyof NavCounts }[] = [
  { label: 'Overview', to: '/', icon: LayoutGrid },
  { label: 'Live Orders', to: '/orders/live', icon: Activity, countKey: 'live' },
  { label: 'Cart Review', to: '/review', icon: ShoppingCart, countKey: 'review' },
  { label: 'Dispatch', to: '/dispatch', icon: MapPin, countKey: 'dispatch' },
  { label: 'Orders', to: '/orders', icon: Receipt },
  { label: 'Stores', to: '/stores', icon: Store },
  { label: 'Customers', to: '/customers', icon: Users },
  { label: 'Platforms', to: '/platforms', icon: Grid3x3 },
  { label: 'Riders & Partners', to: '/partners', icon: Truck },
  { label: 'Payments', to: '/payments', icon: CreditCard, countKey: 'payments' },
  { label: 'Notifications', to: '/notifications', icon: Bell, countKey: 'notifications' },
  { label: 'App Banners', to: '/banners', icon: MonitorSmartphone, countKey: 'banners' },
  { label: 'Settings', to: '/settings/pricing', icon: Settings },
];

type NavCounts = { live: number; review: number; dispatch: number; payments: number; notifications: number; banners: number };

export function Shell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const session = useQuery({ queryKey: ['admin-session'], queryFn: () => adminApi.session() });
  const counts = useQuery({ queryKey: ['admin-nav-counts'], queryFn: () => adminApi.navCounts(), refetchInterval: 15_000 });
  const alerts = useQuery({ queryKey: ['admin-alerts'], queryFn: () => adminApi.alerts(), refetchInterval: 30_000 });

  return (
    <div className="flex h-screen" style={{ background: 'var(--bg)' }}>
      <aside className="w-[220px] shrink-0 border-r flex flex-col" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
        <div className="flex items-center gap-2 px-5 py-4">
          <span className="flex items-end font-display text-xl" style={{ color: 'var(--brand-dark)' }}>
            <img src={wheezW} alt="" className="h-[19px] w-auto mb-px" />heez
          </span>
          <span className="pill pill-neutral">Admin</span>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 space-y-0.5">
          {NAV.map((n) => {
            const active = n.to === '/' ? pathname === '/' : pathname.startsWith(n.to.split('?')[0]);
            const count = n.countKey ? counts.data?.[n.countKey] : undefined;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-[13.5px] font-medium"
                style={active ? { background: 'var(--warn-bg-soft)', color: 'var(--brand-dark)' } : { color: 'var(--text-body)' }}
              >
                <span className="flex items-center gap-2.5">
                  <Icon size={16} strokeWidth={2} />
                  {n.label}
                </span>
                {!!count && <span className="pill pill-warn">{count}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: 'var(--warn-bg)', color: 'var(--warn-strong)' }}
          >
            {session.data?.user.initials ?? '··'}
          </div>
          <div className="text-xs">
            <div className="font-semibold" style={{ color: 'var(--text-heading)' }}>{session.data?.user.name ?? 'Loading…'}</div>
            <div style={{ color: 'var(--text-faint)' }}>{session.data ? `${cap(session.data.role)} · ${session.data.user.email}` : ''}</div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center gap-4 px-6 py-3 border-b" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
          <input placeholder="Search order ID, customer, store, Nova Pay ref" className="flex-1 max-w-md" />
          <div className="flex items-center gap-0.5 text-xs rounded-full p-0.5" style={{ background: 'var(--surface-muted)' }}>
            {['All', 'Food', 'Groceries', 'Pharmacy', 'More'].map((c) => (
              <button
                key={c}
                className="px-3 py-1.5 rounded-full font-medium transition-colors"
                style={
                  c === 'All'
                    ? { background: 'var(--surface)', color: 'var(--text-heading)', boxShadow: 'var(--shadow-card, 0 1px 2px rgba(0,0,0,.06))' }
                    : { background: 'transparent', color: 'var(--text-muted)' }
                }
              >
                {c}
              </button>
            ))}
          </div>
          <select defaultValue="today" className="text-xs">
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <Link to="/notifications" className="relative flex items-center justify-center w-8 h-8 rounded-full" style={{ background: 'var(--surface-muted)' }}>
            <Bell size={16} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
            {!!counts.data?.notifications && (
              <span
                className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                style={{ background: 'var(--danger-strong, #DC2626)' }}
              >
                {counts.data.notifications}
              </span>
            )}
          </Link>
          <div className="pill pill-success">● Live</div>
        </div>
        {!!alerts.data?.items?.length && (
          <div className="flex items-center gap-3 px-6 py-2.5 text-sm" style={{ background: 'var(--warn-bg-soft)', color: 'var(--warn-strong)' }}>
            <span>⚠ {alerts.data.items[0].text}</span>
          </div>
        )}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ');
}
